/**
 * Inline edit panel.
 *
 * One component for every "view, then edit in place" screen, so save, cancel,
 * error handling and the refresh afterwards behave identically everywhere.
 *
 * After a successful save it calls `revalidate()` rather than reloading: the
 * loaders re-run through the same authorized endpoints, so the screen shows
 * what the server actually stored — not what the form hoped it stored.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useRevalidator } from 'react-router';
import { ApiError } from '../api.ts';

export function EditPanel({
  open,
  onClose,
  onSubmit,
  children,
  submitLabel = '保存する',
}: {
  open: boolean;
  onClose: () => void;
  /** Performs the write. Throwing surfaces the message to the user. */
  onSubmit: () => Promise<unknown>;
  children: ReactNode;
  submitLabel?: string;
}) {
  const revalidator = useRevalidator();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit();
      await revalidator.revalidate();
      onClose();
    } catch (err) {
      // A conflict (plan limit, stale row) carries a message worth showing;
      // anything else falls back to the generic text from the API.
      setError(
        err instanceof ApiError
          ? [err.message, ...err.fields].join(' / ')
          : '保存できませんでした',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    // Fades and rises in via CSS. The height:auto animation this used to do is
    // gone deliberately: it is the one thing CSS cannot express, and it was
    // animating layout on every keystroke-adjacent re-render to buy very little.
    <form onSubmit={(event) => void submit(event)} className="editpanel animate-fade-up">
      <div className="editpanel__body">{children}</div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="editpanel__actions">
        <button className="btn" type="submit" disabled={saving}>
          {saving ? '保存中…' : submitLabel}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onClose} disabled={saving}>
          キャンセル
        </button>
      </div>
    </form>
  );
}

/**
 * A labelled field; `hint` is where inheritance and format rules are explained.
 *
 * `span` is how a field opts out of the panel's auto-fitted columns. A textarea
 * or a long explanation wants the whole row; leaving it to share a 220px column
 * with a one-line input is what turns a form into a puzzle.
 */
export function Field({
  label,
  htmlFor,
  hint,
  required,
  span,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  required?: boolean;
  /** 'full' takes the whole row; 'wide' takes two columns where there are two. */
  span?: 'full' | 'wide';
  children: ReactNode;
}) {
  return (
    <div className={`field${span ? ` field--${span}` : ''}`}>
      <label htmlFor={htmlFor}>
        {label}
        {required && <span className="req">必須</span>}
      </label>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

/**
 * A heading inside a form.
 *
 * Long forms are read in groups, and a group with no name is read as one long
 * list. Always full width, because a heading that shares a row with a text input
 * is not a heading.
 */
export function FieldGroup({ title, note }: { title: string; note?: string }) {
  return (
    <p className="fieldgroup">
      <span className="fieldgroup__title">{title}</span>
      {note && <span className="fieldgroup__note">{note}</span>}
    </p>
  );
}
